import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Lock, CheckCircle, AlertCircle } from "lucide-react";

const DEFAULT_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripeCache = new Map();
const getStripePromise = (key) => {
  const pk = key || DEFAULT_PK;
  if (!stripeCache.has(pk)) stripeCache.set(pk, loadStripe(pk));
  return stripeCache.get(pk);
};

function CheckoutForm({ onSuccess, onCancel, amount, currency = "EUR", bookingId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const returnUrl = bookingId
      ? `${window.location.origin}/booking/success?booking_id=${bookingId}`
      : `${window.location.origin}/booking/success`;

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: "if_required",
    });

    if (result.error) {
      setError(result.error.message);
      setProcessing(false);
    } else if (result.paymentIntent?.status === "succeeded") {
      onSuccess(result.paymentIntent);
    } else {
      setError(t('checkout.statusUnclear', 'Payment status unclear. Please check your bookings.'));
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="stripe-checkout-form" className="notranslate" translate="no">
      <PaymentElement
        options={{
          layout: "tabs",
          defaultValues: { billingDetails: { address: { country: "NL" } } },
        }}
      />
      {error && (
        <div className="flex items-center gap-2 mt-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 text-sm" data-testid="payment-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      <div className="flex gap-3 mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
          className="flex-1"
          data-testid="payment-cancel-btn"
        >
          {t('checkout.cancel', 'Cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 gap-2"
          data-testid="payment-submit-btn"
        >
          {processing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Lock className="w-4 h-4" />
          )}
          {processing ? t('checkout.processing', 'Processing...') : `${t('checkout.pay', 'Pay')} ${currency} ${amount?.toFixed(2)}`}
        </Button>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> {t('checkout.securedByStripe', 'Secured by Stripe')}
      </p>
    </form>
  );
}

export default function EmbeddedCheckout({
  clientSecret,
  amount,
  currency = "EUR",
  onSuccess,
  onCancel,
  bookingDetails,
  bookingId,
  publishableKey,
}) {
  const { t } = useTranslation();
  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  if (!clientSecret) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-primary" />
          <p className="text-muted-foreground">{t('checkout.loading', 'Loading payment form...')}</p>
        </CardContent>
      </Card>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#1e3a5f",
        colorBackground: "#ffffff",
        colorText: "#1a1a1a",
        borderRadius: "8px",
        fontFamily: "system-ui, -apple-system, sans-serif",
      },
    },
  };

  return (
    <Card className="border-[#00A9E0]/20 notranslate" data-testid="embedded-checkout" translate="no">
      <CardContent className="p-6">
        {bookingDetails && (
          <div className="mb-6 pb-4 border-b">
            <h3 className="font-semibold text-lg mb-2">{bookingDetails.hotelName}</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              {bookingDetails.roomType && <p>{bookingDetails.roomType}</p>}
              {bookingDetails.dates && <p>{bookingDetails.dates}</p>}
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">{t('checkout.total', 'Total')}</span>
              <span className="text-2xl font-bold">{currency} {amount?.toFixed(2)}</span>
            </div>
          </div>
        )}
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm
            onSuccess={onSuccess}
            onCancel={onCancel}
            amount={amount}
            currency={currency}
            bookingId={bookingId}
          />
        </Elements>
      </CardContent>
    </Card>
  );
}
